import React, { useEffect, useState, useMemo } from 'react';
import { agruparContasPorDV, resolverContaSelecionada } from '../utils/contas';
import { Modal, Form, Input, DatePicker, Select, InputNumber, Row, Col, Space, Divider, Typography, Upload, Button, message, Tooltip, Card } from 'antd';
import {
    SaveOutlined,
    ArrowLeftOutlined,
    UploadOutlined,
    DollarOutlined,
    CalendarOutlined,
    FileTextOutlined,
    InboxOutlined,
    DownloadOutlined,
    DeleteOutlined,
    PaperClipOutlined,
    HistoryOutlined,
    UserOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import { Method_POST_CONTEXTO, Method_POST_UPLOAD, Method_GET_DOWNLOAD_URL } from '../../../../../service/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export default function LancamentoModal({
    open,
    onCancel,
    onSave,
    mode = 'new',
    type = 'D',
    initialData = null,
    isContador = false
}) {
    const [form] = Form.useForm();
    const [grupos, setGrupos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [contas, setContas] = useState([]);
    const [loadingContas, setLoadingContas] = useState(false);
    // Unifica a mesma conta duplicada por causa do DV (OFX × Open Finance).
    // O `id` de cada grupo é um id REAL, então salvar continua funcionando.
    const contasView = useMemo(() => agruparContasPorDV(contas), [contas]);
    const [fileList, setFileList] = useState([]);
    const [serverFiles, setServerFiles] = useState([]);
    const [saving, setSaving] = useState(false);

    const isView = mode === 'view';
    const isEdit = mode === 'edit';
    const isCredit = type === 'C';

    // Carrega grupos quando o modal abre
    useEffect(() => {
        if (open && type) {
            Method_POST_CONTEXTO("/contabil/lancamento/categoria/grupo/listar", {
                tipo: { id: type },
            })
                .then((res) => {
                    const formatted = (res || []).map(g => ({ ...g, id: String(g.id) }));
                    setGrupos(formatted);
                })
                .catch(() => message.error("Erro ao buscar grupos"));
        }
    }, [open, type]);

    // Carrega contas bancárias quando o modal abre
    useEffect(() => {
        if (open) {
            setLoadingContas(true);
            Method_POST_CONTEXTO("/cliente/conta-bancaria/listar")
                .then((res) => setContas(Array.isArray(res) ? res : []))
                .catch(() => message.error("Erro ao carregar contas do cliente"))
                .finally(() => setLoadingContas(false));
        }
    }, [open]);

    // Preenche o formulário ao editar/visualizar
    useEffect(() => {
        if (open && initialData) {
            const situacaoId = initialData.situacao?.id || initialData.situacao;
            // Resolve por id OU por agência+conta — o Pantheon manda a conta do
            // lançamento SEM id, e antes isso caía sempre em "000 - Caixa".
            // (Se as contas ainda não carregaram, o efeito abaixo reajusta.)
            const contaId = resolverContaSelecionada(initialData.contaBancaria, contasView);
            const categoriaId = initialData.categoria?.id ? String(initialData.categoria.id) : (initialData.categoria ? String(initialData.categoria) : undefined);
            const grupoId = initialData.grupo?.id ? String(initialData.grupo.id) : (initialData.categoria?.grupo?.id ? String(initialData.categoria.grupo.id) : undefined);

            if (initialData.categoria && typeof initialData.categoria === 'object' && initialData.categoria.id) {
                setCategorias([{
                    ...initialData.categoria,
                    id: String(initialData.categoria.id)
                }]);
            }

            form.setFieldsValue({
                ...initialData,
                dataReferencia: initialData.dataReferencia ? dayjs(initialData.dataReferencia) : null,
                situacao: situacaoId,
                contaBancaria: contaId,
                grupo: grupoId,
                categoria: categoriaId
            });

            setFileList([]);
            setServerFiles([]);

            if (grupoId) {
                handleGrupoChange(grupoId, false);
            }
        } else if (open) {
            form.resetFields();
            form.setFieldsValue({
                dataReferencia: dayjs(),
                situacao: 'A',
                contaBancaria: "0"
            });
            setCategorias([]);
            setFileList([]);
            setServerFiles([]);
        }
    }, [open, initialData, form]);

    // As contas chegam por request assíncrono; quando chegam, reajusta SÓ o
    // campo da conta (sem tocar no resto do formulário, pra não apagar o que o
    // usuário já digitou). Sem isso, abrir o modal antes do load deixava "Caixa".
    useEffect(() => {
        if (!open || !initialData || contasView.length === 0) return;
        form.setFieldsValue({
            contaBancaria: resolverContaSelecionada(initialData.contaBancaria, contasView),
        });
    }, [open, initialData, contasView, form]);

    // Busca arquivos já salvos no servidor
    useEffect(() => {
        if (open && initialData?.id && (mode === 'edit' || mode === 'view')) {
            Method_POST_CONTEXTO("/contabil/lancamento/arquivo/listar", {
                id: initialData.id
            })
                .then((res) => {
                    if (res && res.files) {
                        const files = Array.isArray(res.files) ? res.files : [res.files];
                        setServerFiles(files);
                    } else {
                        setServerFiles([]);
                    }
                })
                .catch((err) => {
                    console.error("Erro ao carregar lista de arquivos:", err);
                    setServerFiles([]);
                });
        } else {
            setServerFiles([]);
        }
    }, [open, initialData, mode]);

    const handleDownloadFile = async (fileName) => {
        try {
            const path = `/contabil/lancamento/arquivo/download?id=${initialData.id}&filename=${fileName}`;
            await Method_GET_DOWNLOAD_URL(path, fileName);
        } catch (error) {
            console.error('Erro ao baixar arquivo:', error);
            message.error('Erro ao baixar o arquivo.');
        }
    };

    const handleRemoveServerFile = (fileName) => {
        Modal.confirm({
            title: 'Confirmação',
            content: `Confirma a exclusão do arquivo "${fileName}"?`,
            okText: 'Sim',
            cancelText: 'Não',
            centered: true,
            onOk() {
                const url = `contabil/lancamento/arquivo/remover?id=${initialData.id}&filename=${fileName}`;
                return Method_POST_CONTEXTO(url, {})
                    .then(() => {
                        message.success('Arquivo removido com sucesso');
                        setServerFiles(prev => prev.filter(f => {
                            const name = typeof f === 'string' ? f.split('\\').pop().split('/').pop() : f;
                            return name !== fileName;
                        }));
                    })
                    .catch(() => message.error('Erro ao remover arquivo'));
            }
        });
    };

    const handleUploadChange = ({ fileList }) => setFileList(fileList);

    const handleGrupoChange = (grupoId, resetCategoria = true) => {
        if (!grupoId) {
            setCategorias([]);
            if (resetCategoria) form.setFieldsValue({ categoria: null });
            return;
        }

        Method_POST_CONTEXTO("/contabil/lancamento/categoria/listar", {
            grupo: { id: grupoId },
            situacao: { id: "A" },
        })
            .then((res) => {
                const formatted = (res || []).map(c => ({ ...c, id: String(c.id) }));
                setCategorias(formatted);
            })
            .catch(() => message.error("Erro ao buscar categorias"));

        if (resetCategoria) form.setFieldsValue({ categoria: null });
    };

    const handleSubmit = () => {
        form.validateFields()
            .then((values) => {
                setSaving(true);
                let situacaoFinal = values.situacao;

                const oldCategory = initialData?.categoria?.id || initialData?.categoria;
                const newCategory = values.categoria;

                if (!isContador && initialData) {
                    const hasChanges =
                        newCategory !== oldCategory ||
                        values.contaBancaria !== (initialData.contaBancaria?.id || initialData.contaBancaria) ||
                        !dayjs(values.dataReferencia).isSame(dayjs(initialData.dataReferencia), 'day') ||
                        values.valor?.toString() !== initialData.valor?.toString() ||
                        values.descricao !== initialData.descricao;

                    if (hasChanges) {
                        situacaoFinal = 'L';
                    }
                }

                // Oferecer criar regra automática ao reclassificar
                if (isEdit && initialData && newCategory !== oldCategory) {
                    // let ruleDescription = values.descricao || "";
                    // Modal.confirm({
                    //     title: 'Criar Regra Automática?',
                    //     width: 450,
                    //     content: (
                    //         <div style={{ marginTop: 16 }}>
                    //             <Text type="secondary">Deseja criar uma regra para classificar transações similares automaticamente? Você pode ajustar a descrição abaixo para ser mais genérica.</Text>
                    //             <div style={{ marginTop: 16 }}>
                    //                 <Text strong>Descrição da Regra (Termo de Busca):</Text>
                    //                 <Input
                    //                     style={{ marginTop: 8 }}
                    //                     defaultValue={ruleDescription}
                    //                     onChange={(e) => { ruleDescription = e.target.value }}
                    //                     placeholder="Ex: DETRAN"
                    //                 />
                    //             </div>
                    //         </div>
                    //     ),
                    //     okText: 'Criar Regra',
                    //     cancelText: 'Agora Não',
                    //     onOk() {
                    //         const rulePayload = {
                    //             descricao: ruleDescription,
                    //             erpCategoriaId: newCategory,
                    //             tipoId: type
                    //         };

                    //         return Method_POST_CONTEXTO("/openfinance/criar-regra", rulePayload)
                    //             .then((res) => {
                    //                 if (res?.message) {
                    //                     message.success(res.message);
                    //                 }
                    //             })
                    //             .catch((err) => {
                    //                 console.error("Erro ao criar regra de categoria:", err);
                    //                 message.error("Erro ao processar solicitação de regra.");
                    //             });
                    //     },
                    // });
                }

                const payload = {
                    ...(isEdit && initialData ? initialData : {}),
                    id: isEdit ? initialData.id : "",
                    cliente: initialData?.cliente || { id: "" },
                    categoria: { id: values.categoria },
                    tipo: isEdit && initialData?.tipo ? initialData.tipo : { id: type },
                    contaBancaria: { id: values.contaBancaria || "0" },
                    dataReferencia: values.dataReferencia ? values.dataReferencia.toISOString() : null,
                    descricao: values.descricao || "",
                    situacao: { id: situacaoFinal },
                    valor: values.valor?.toString() || "0.00",
                    // CRITICO: garante que pluggyTransactionId seja preservado mesmo
                    // se o spread acima nao incluir (caso o GET nao retorne o campo).
                    // Sem isso, edicao do contador apagava o vinculo Pluggy e a proxima
                    // sincronizacao criava lancamento duplicado.
                    pluggyTransactionId: initialData?.pluggyTransactionId ?? null,
                };

                // Usa o `mode` como critério principal — evita criar duplicata se o id vier vazio
                const url = isEdit ? "contabil/lancamento/atualizar" : "contabil/lancamento/cadastrar";

                Method_POST_CONTEXTO(url, payload)
                    .then(async (res) => {
                        const lancamentoId = isEdit ? initialData?.id : res?.id;

                        // Faz upload ANTES de fechar o modal
                        if (lancamentoId && fileList.length > 0) {
                            try {
                                await uploadArquivo(lancamentoId);
                                message.success('Arquivo(s) enviado(s) com sucesso!');
                            } catch (e) {
                                console.error('Erro no upload:', e);
                            }
                        }

                        message.success(`Lançamento ${isEdit ? 'atualizado' : 'cadastrado'} com sucesso!`);
                        onSave?.();
                        if (isEdit) {
                            onCancel();
                        } else {
                            form.resetFields();
                            form.setFieldsValue({
                                dataReferencia: dayjs(),
                                situacao: 'A',
                                contaBancaria: "0"
                            });
                            setFileList([]);
                        }
                    })
                    .catch(() => {
                        message.error("Erro ao processar lançamento");
                    })
                    .finally(() => setSaving(false));
            })
            .catch((info) => console.log("Validation Failed:", info));
    };

    const uploadArquivo = async (lancamentoId) => {
        if (!fileList.length || !lancamentoId) return;

        for (const file of fileList) {
            const fileToUpload = file.originFileObj || file;
            const v_url = `contabil/lancamento/arquivo/upload?id=${lancamentoId}`;
            try {
                const res = await Method_POST_UPLOAD(v_url, fileToUpload);
                if (res && res.erro) {
                    const errorMsg = `${res.erro.id}: ${res.erro.mensagem}`;
                    if (res.erro.categoria && res.erro.categoria.id === 'B') {
                        message.warning(errorMsg);
                    } else {
                        message.error(errorMsg);
                    }
                }
            } catch (error) {
                console.error("Erro no processamento do arquivo:", error);
                message.error("Erro ao enviar arquivo");
            }
        }
    };

    const accentColor = isCredit ? '#059669' : '#e11d48';
    const bgColor = isCredit ? '#ecfdf5' : '#fff1f2';

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            width={1000}
            style={{ top: 20 }}
            className="premium-modal-v2"
            destroyOnClose
            closable={true}
        >
            <div style={{ padding: '8px' }}>
                <header style={{
                    marginBottom: 32,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: 20
                }}>
                    <div style={{
                        width: 56,
                        height: 56,
                        borderRadius: 16,
                        backgroundColor: bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: accentColor,
                        fontSize: 24,
                        boxShadow: `0 4px 12px ${accentColor}20`
                    }}>
                        {isCredit ? <DollarOutlined /> : <FileTextOutlined />}
                    </div>
                    <div>
                        <Title level={4} style={{ margin: 0, color: '#0f172a' }}>
                            {isView ? 'Detalhes do Lançamento' : (isEdit ? 'Alterar Lançamento' : `Novo ${isCredit ? 'Recebimento' : 'Pagamento'}`)}
                        </Title>
                        <Text type="secondary">Preencha as informações financeiras abaixo para o controle contábil.</Text>
                    </div>
                </header>

                <Form
                    form={form}
                    layout="vertical"
                    disabled={isView}
                    requiredMark={false}
                >
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="grupo"
                                label={<Space><CalendarOutlined /> Grupo de Lançamento</Space>}
                                rules={[{ required: true, message: 'Obrigatório' }]}
                            >
                                <Select
                                    size="large"
                                    className="premium-select"
                                    placeholder="Selecione o grupo"
                                    onChange={(val) => handleGrupoChange(val)}
                                >
                                    {grupos.map(g => (
                                        <Select.Option key={g.id} value={String(g.id)}>{g.nome}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="categoria"
                                label={<Space><FileTextOutlined /> Categoria / Conta</Space>}
                                rules={[{ required: true, message: 'Obrigatório' }]}
                            >
                                <Select
                                    size="large"
                                    className="premium-select"
                                    placeholder="Selecione a categoria"
                                    disabled={!categorias.length}
                                >
                                    {categorias.map(c => (
                                        <Select.Option key={c.id} value={String(c.id)}>{c.nome}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="contaBancaria"
                                label={<Space><DollarOutlined /> Conta / Forma</Space>}
                                rules={[{ required: true, message: 'Obrigatório' }]}
                            >
                                <Select size="large" className="premium-select" loading={loadingContas}>
                                    <Select.Option value="0">000 - Dinheiro (Caixa)</Select.Option>
                                    {contasView.map(acc => (
                                        <Select.Option key={acc.id} value={String(acc.id)}>
                                            {acc.nome || acc.banco?.nome} - Ag: {acc.agencia} CC: {acc.conta}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[24, 0]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="dataReferencia"
                                label="Data de Referência"
                                rules={[{ required: true, message: 'Obrigatório' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format={["DD/MM/YYYY", "DDMMYYYY"]}
                                    size="large"
                                    className="premium-datepicker"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="valor"
                                label="Valor do Lançamento"
                                rules={[{ required: true, message: 'Obrigatório' }]}
                            >
                                <InputNumber
                                    prefix="R$"
                                    style={{ width: '100%' }}
                                    className="premium-input-number"
                                    size="large"
                                    placeholder="0,00"
                                    decimalSeparator=","
                                    groupSeparator="."
                                    precision={2}
                                    step={0.01}
                                    formatter={value => value ? `${value}`.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''}
                                    parser={value => value ? value.replace(/\./g, '').replace(',', '.') : ''}
                                />
                            </Form.Item>
                        </Col>
                        {isContador && (
                            <Col xs={24} md={8}>
                                <Form.Item
                                    name="situacao"
                                    label="Situação Atual"
                                    rules={[{ required: true, message: 'Obrigatório' }]}
                                >
                                    <Select size="large" className="premium-select">
                                        <Select.Option value="A">Aberto</Select.Option>
                                        <Select.Option value="L">Liquidado</Select.Option>
                                        <Select.Option value="C">Cancelado</Select.Option>
                                        <Select.Option value="N">Não Identificado</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                    </Row>

                    <Form.Item
                        name="descricao"
                        label="Descrição / Histórico"
                        rules={[{ required: true, message: 'Descreva o lançamento' }]}
                    >
                        <Input.TextArea rows={2} placeholder="Ex: Pagamento referente a nota fiscal #123..." className="premium-textarea" />
                    </Form.Item>

                    <div style={{ marginTop: 24, marginBottom: 24 }}>
                        <Divider orientation="left"><Text strong style={{ color: '#475569' }}><PaperClipOutlined /> Documentos e Comprovantes (Opcional)</Text></Divider>

                        {/* Lista de arquivos já salvos no servidor */}
                        {serverFiles.length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                                {serverFiles.map((f, i) => {
                                    const fileName = typeof f === 'string' ? f.split('\\').pop().split('/').pop() : f;
                                    return (
                                        <div key={`server-${i}`} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '10px 16px',
                                            background: '#f8fafc',
                                            borderRadius: '8px',
                                            border: '1px solid #e2e8f0',
                                            marginBottom: 8
                                        }}>
                                            <Space>
                                                <PaperClipOutlined style={{ color: '#64748b' }} />
                                                <Text style={{ color: '#334155' }}>{fileName}</Text>
                                                <Text type="secondary" style={{ fontSize: 11 }}>(servidor)</Text>
                                            </Space>
                                            <Space size="small">
                                                <Tooltip title="Baixar arquivo">
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        icon={<DownloadOutlined />}
                                                        onClick={() => handleDownloadFile(fileName)}
                                                        style={{ borderRadius: 6 }}
                                                    />
                                                </Tooltip>
                                                {!isView && (
                                                    <Tooltip title="Remover">
                                                        <Button
                                                            type="primary"
                                                            danger
                                                            size="small"
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => handleRemoveServerFile(fileName)}
                                                            style={{ borderRadius: 6 }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Space>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Upload de novos arquivos */}
                        {!isView && (
                            <Upload.Dragger
                                multiple={true}
                                beforeUpload={() => false}
                                fileList={fileList}
                                onChange={handleUploadChange}
                                className="premium-upload-dragger"
                            >
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined style={{ color: '#005A9E' }} />
                                </p>
                                <p className="ant-upload-text">Clique ou arraste comprovantes aqui</p>
                                <p className="ant-upload-hint" style={{ fontSize: 12, color: '#94a3b8' }}>Formatos aceitos: PDF, imagens, planilhas (máx 5MB)</p>
                            </Upload.Dragger>
                        )}
                        {isView && serverFiles.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '40px', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                                <FileTextOutlined style={{ fontSize: 32, color: '#94a3b8', marginBottom: 8 }} />
                                <p style={{ color: '#64748b', margin: 0 }}>Nenhum documento anexado a este lançamento.</p>
                            </div>
                        )}
                    </div>
                </Form>

                {/* Seção de Logs (Apenas para Contadores) */}
                {isContador && (isEdit || isView) && initialData?.id && (
                    <div style={{ marginTop: 32 }}>
                        <Divider orientation="left">
                            <Text strong style={{ color: '#475569' }}>
                                <HistoryOutlined /> Histórico de Auditoria (Contabilidade)
                            </Text>
                        </Divider>

                        <div style={{ marginTop: 24, marginBottom: 24 }}>
                            <Card
                                size="small"
                                style={{
                                    background: '#f8fafc',
                                    borderRadius: '12px',
                                    border: '1px solid #e2e8f0'
                                }}
                            >
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12}>
                                        <Space direction="vertical" size={0}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Cadastrado por:</Text>
                                            <Space>
                                                <UserOutlined style={{ color: '#64748b' }} />
                                                <Text strong>{initialData?.usuarioCadastro?.nome || initialData?.usuarioCadastro?.id || (typeof initialData?.usuarioCadastro === 'string' ? initialData.usuarioCadastro : 'Não identificado')}</Text>
                                            </Space>
                                        </Space>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Space direction="vertical" size={0}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Data de Cadastro:</Text>
                                            <Space>
                                                <ClockCircleOutlined style={{ color: '#64748b' }} />
                                                <Text strong>{initialData?.dataCadastro ? dayjs(initialData.dataCadastro).format('DD/MM/YYYY HH:mm') : 'Não identificada'}</Text>
                                            </Space>
                                        </Space>
                                    </Col>

                                    {(initialData?.usuarioAlteracao?.id || initialData?.dataAlteracao) && (
                                        <>
                                            <Col xs={24}><Divider style={{ margin: '8px 0', borderStyle: 'dashed' }} /></Col>
                                            <Col xs={24} sm={12}>
                                                <Space direction="vertical" size={0}>
                                                    <Text type="secondary" style={{ fontSize: 11 }}>Última alteração por:</Text>
                                                    <Space>
                                                        <UserOutlined style={{ color: '#faad14' }} />
                                                        <Text strong>{initialData?.usuarioAlteracao?.nome || initialData?.usuarioAlteracao?.id || (typeof initialData?.usuarioAlteracao === 'string' ? initialData.usuarioAlteracao : 'Sistema')}</Text>
                                                    </Space>
                                                </Space>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Space direction="vertical" size={0}>
                                                    <Text type="secondary" style={{ fontSize: 11 }}>Data da alteração:</Text>
                                                    <Space>
                                                        <ClockCircleOutlined style={{ color: '#faad14' }} />
                                                        <Text strong>{initialData?.dataAlteracao ? dayjs(initialData.dataAlteracao).format('DD/MM/YYYY HH:mm') : 'Não identificada'}</Text>
                                                    </Space>
                                                </Space>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </Card>
                        </div>
                    </div>
                )}

                <div
                    className="footer-buttons"
                    style={{
                        marginTop: 40,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 12,
                        paddingTop: 24,
                        borderTop: '1px solid #f1f5f9',
                        flexWrap: 'wrap'
                    }}
                >
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={onCancel}
                        size="large"
                        className="premium-btn-secondary"
                    >
                        Voltar
                    </Button>
                    {!isView && (
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSubmit}
                            size="large"
                            loading={saving}
                            className="premium-btn-primary"
                        >
                            {isEdit ? 'Salvar Alterações' : 'Realizar Lançamento'}
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
}
